import { Box, Flex, Text } from "@chakra-ui/react";
import Link from "next/link";
import type { ReactElement } from "react";

import type {
  PublicDestinationOperatorRow,
  PublicDestinationOperators,
} from "@otalytics/shared";

import { Meta } from "./data-row";

interface TopOperatorsProps {
  operators: PublicDestinationOperators | null;
  /** Section heading — differs between the free teaser and the gated full list. */
  title: string;
  /** One sentence under the heading. */
  blurb: string;
  /** Optional guide under the blurb. Omitted when the platform has no matching post. */
  link?: { href: string; label: string } | null;
}

/**
 * Who owns the first page.
 *
 * Ranked by share of the reviews the tours picked up over the window, not by
 * lifetime review totals: a supplier that banked its reviews three years ago
 * is not the one taking the bookings today. Slots held and best rank sit
 * beside the share, because a share alone cannot say how much of the page a
 * name actually occupies.
 *
 * Split into leaders and a tail, because a flat list of equal rows makes the
 * operator holding 41% of the page look like the one holding 3%. The first
 * three get a rank gutter, a full-width magnitude bar and their supporting
 * counts; everyone after them is one line, two abreast, since the tail's job
 * is completeness rather than emphasis. That also stops seven rows of
 * near-identical text running down the left third of a 1140px column.
 *
 * Deliberately not three cards across: equal thirds is the most templated
 * shape on the web, and this page's language is hairline rows throughout —
 * the only card treatment it earns is the bento, where the tiles vary in span.
 */
export function TopOperators({
  operators,
  title,
  blurb,
  link,
}: TopOperatorsProps): ReactElement | null {
  // An empty shell reads as broken, so render nothing at all.
  if (!operators || operators.rows.length === 0) {
    return null;
  }

  // A column with no data in any row is dropped, never rendered as dashes.
  const hasReviewShare = operators.rows.some(
    (row) => row.reviewSharePct != null
  );
  const hasGmv = operators.rows.some((row) => row.gmvSharePct != null);

  // Same rule, one step further: a column whose value is identical and trivial
  // on every row is not data either. When nobody holds more than one tour,
  // "1 tour on the page" seven times is noise that crowds out best rank.
  const hasMultiTourOperator = operators.rows.some((row) => row.tourCount > 1);
  const { coverage } = operators;

  // With a share column, rows with 0% of new reviews add nothing per-row —
  // truncate after the last row that carries a positive share and fold the
  // rest into one summary line. Without a share column the order is by
  // first-page slots, so every row still carries its ranking figure and none
  // is dropped.
  let visibleRows = operators.rows;
  let droppedRows: PublicDestinationOperatorRow[] = [];
  if (hasReviewShare) {
    let lastPositive = -1;
    operators.rows.forEach((row, index) => {
      if ((row.reviewSharePct ?? 0) > 0) {
        lastPositive = index;
      }
    });
    visibleRows = operators.rows.slice(0, lastPositive + 1);
    droppedRows = operators.rows.slice(lastPositive + 1);
  }
  const droppedSlots = droppedRows.reduce((sum, row) => sum + row.tourCount, 0);

  // Three, or fewer when the page is thin. A "leaders" group of two out of a
  // list of three is a distinction without a difference, so below five rows
  // the split is dropped and everything renders as a leader.
  const leaders = visibleRows.length < 5 ? visibleRows : visibleRows.slice(0, 3);
  const tail = visibleRows.length < 5 ? [] : visibleRows.slice(3);

  // The footer states the ranking basis, not just the denominator. Without the
  // share column there are no "shares" to describe, and the order has fallen
  // back to first-page slots — saying otherwise would leave the section
  // claiming a demand ranking it did not perform. `hasGmv` implies
  // `hasReviewShare` (turnover needs a review delta to exist), so the share
  // branch covers every case where any figure is published.
  const basis = hasReviewShare
    ? `shares across ${coverage.toursAttributed} of ${coverage.toursOnPage} ranked tours — the ones whose operator we know · review shares from ${coverage.toursWithReviewSignal} with 30-day review history`
    : `ranked by first-page slots held, across ${coverage.toursAttributed} of ${coverage.toursOnPage} ranked tours — the ones whose operator we know · too little of the page has 30-day review history to rank by new reviews`;

  return (
    <Box>
      <Text
        as="h2"
        fontFamily="var(--font-display)"
        fontSize={{ base: "20px", md: "22px" }}
        fontWeight="600"
        letterSpacing="-0.02em"
        color="var(--color-text-primary)"
        mb={2}
      >
        {title}
      </Text>
      <Text
        fontSize={{ base: "15px", md: "16px" }}
        lineHeight="1.6"
        color="var(--color-text-secondary)"
        maxW="60ch"
        mb={link ? 2 : { base: 5, md: 6 }}
      >
        {blurb}
      </Text>
      {link && (
        <Link href={link.href}>
          <Text
            mb={{ base: 5, md: 6 }}
            fontSize="15px"
            color="var(--color-primary)"
            _hover={{ textDecoration: "underline" }}
          >
            {link.label}
          </Text>
        </Link>
      )}

      <Box>
        {leaders.map((row, index) => (
          <LeaderRow
            key={row.name}
            row={row}
            rank={index + 1}
            hasReviewShare={hasReviewShare}
            hasGmv={hasGmv}
            showTourCount={hasMultiTourOperator}
          />
        ))}
      </Box>

      {tail.length > 0 && (
        <Box mt={{ base: 6, md: 8 }}>
          <Text
            fontFamily="var(--font-geist-mono)"
            fontSize="11px"
            letterSpacing="0.04em"
            color="var(--color-text-tertiary)"
            mb={2}
          >
            {`and ${tail.length} more`}
          </Text>
          {/* Two abreast: one line each, so the tail costs half the height and
              spends the width the single column was leaving empty. */}
          <Box
            display="grid"
            gridTemplateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }}
            columnGap={{ md: 10 }}
          >
            {tail.map((row, index) => (
              <TailRow
                key={row.name}
                row={row}
                rank={leaders.length + index + 1}
                hasReviewShare={hasReviewShare}
                showTourCount={hasMultiTourOperator}
              />
            ))}
          </Box>
        </Box>
      )}

      {droppedRows.length > 0 && (
        <Box mt={{ base: 3, md: 4 }}>
          <Meta>
            {droppedRows.length} more{" "}
            {droppedRows.length === 1 ? "operator holds" : "operators hold"}{" "}
            {droppedSlots} first-page{" "}
            {droppedSlots === 1 ? "slot" : "slots"} between them, 0% of new
            reviews.
          </Meta>
        </Box>
      )}

      <Text
        className="tabular-nums"
        fontFamily="var(--font-geist-mono)"
        fontSize="11px"
        color="var(--color-text-tertiary)"
        mt={{ base: 3, md: 4 }}
      >
        {basis}
      </Text>
    </Box>
  );
}

/** The rank gutter both variants share, so the two groups stay on one grid. */
function Rank({
  rank,
  size,
}: {
  rank: number;
  size: "lead" | "tail";
}): ReactElement {
  return (
    <Text
      className="tabular-nums"
      fontFamily="var(--font-geist-mono)"
      fontSize={size === "lead" ? "13px" : "12px"}
      color="var(--color-text-tertiary)"
      w={size === "lead" ? "24px" : "20px"}
      flexShrink={0}
      lineHeight="1.5"
    >
      {rank}
    </Text>
  );
}

/**
 * One of the first three. The share leads, but never without the review count
 * it is drawn from — across a thin page a percentage is one tour wide.
 *
 * The magnitude bar spans the full row rather than sitting in the right-hand
 * column. At 1140px a 200px bar renders a 41% share as 82px of colour, which
 * reads as a detail; across the row the same share is the widest thing in the
 * section, which is what it actually is.
 */
function LeaderRow({
  row,
  rank,
  hasReviewShare,
  hasGmv,
  showTourCount,
}: {
  row: PublicDestinationOperatorRow;
  rank: number;
  hasReviewShare: boolean;
  hasGmv: boolean;
  showTourCount: boolean;
}): ReactElement {
  const showsShare = hasReviewShare && row.reviewSharePct != null;
  const showsGmv = hasGmv && row.gmvSharePct != null;

  return (
    <Box py={{ base: 3, md: 4 }} borderBottom="1px solid var(--color-border-subtle)">
      <Flex alignItems="baseline" gap={3}>
        <Rank rank={rank} size="lead" />

        <Box minW={0} flex="1">
          <Text
            fontSize={{ base: "16px", md: "17px" }}
            fontWeight="600"
            lineHeight="1.4"
            color="var(--color-text-primary)"
            style={{ overflowWrap: "break-word" }}
          >
            {row.name}
          </Text>
          {/* Without a share the figure on the right IS best rank, so repeating
              it here put "#2" on the row twice. The meta line only carries what
              the figure does not already say, and disappears when that is
              nothing. */}
          {(showTourCount || showsShare || showsGmv) && (
            <Flex gap={3} flexWrap="wrap" mt={1}>
              {showTourCount && (
                <Meta>
                  {row.tourCount} {row.tourCount === 1 ? "tour" : "tours"} on the
                  page
                </Meta>
              )}
              {showsShare && <Meta>best rank #{row.bestPosition}</Meta>}
              {showsShare && <Meta>{row.newReviews} new reviews in 30 days</Meta>}
              {showsGmv && <Meta>{row.gmvSharePct}% of estimated turnover</Meta>}
            </Flex>
          )}
        </Box>

        <Flex alignItems="baseline" gap={2} flexShrink={0}>
          <Text
            className="tabular-nums"
            fontFamily="var(--font-display)"
            fontSize={{ base: "19px", md: "21px" }}
            fontWeight="600"
            letterSpacing="-0.03em"
            lineHeight="1"
            color="var(--color-text-primary)"
          >
            {showsShare ? `${row.reviewSharePct}%` : `#${row.bestPosition}`}
          </Text>
          <Text
            fontSize="13px"
            color="var(--color-text-tertiary)"
            display={{ base: "none", sm: "block" }}
          >
            {showsShare ? "of new reviews" : "best rank"}
          </Text>
        </Flex>
      </Flex>

      {showsShare && (
        <Box
          h="4px"
          borderRadius="999px"
          bg="var(--color-border)"
          w="100%"
          mt={3}
          role="img"
          aria-label={`${row.name} took ${row.reviewSharePct}% of the new reviews on this page`}
        >
          <Box
            h="100%"
            borderRadius="999px"
            bg="var(--color-accent-primary)"
            w={`${Math.min(100, row.reviewSharePct!)}%`}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * Everyone after the third. One line, because the tail's job is completeness:
 * a reader scanning it wants to know whether a name is on the page at all, not
 * to study it.
 */
function TailRow({
  row,
  rank,
  hasReviewShare,
  showTourCount,
}: {
  row: PublicDestinationOperatorRow;
  rank: number;
  hasReviewShare: boolean;
  showTourCount: boolean;
}): ReactElement {
  const showsShare = hasReviewShare && row.reviewSharePct != null;

  return (
    <Flex
      alignItems="baseline"
      gap={3}
      py={2}
      borderBottom="1px solid var(--color-border-subtle)"
    >
      <Rank rank={rank} size="tail" />
      <Text
        fontSize="14px"
        fontWeight="500"
        color="var(--color-text-primary)"
        flex="1"
        minW={0}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        title={row.name}
      >
        {row.name}
      </Text>
      <Text
        className="tabular-nums"
        fontFamily="var(--font-geist-mono)"
        fontSize="12px"
        color="var(--color-text-tertiary)"
        flexShrink={0}
        whiteSpace="nowrap"
      >
        {showTourCount ? `${row.tourCount}× · ` : ""}
        {showsShare ? `${row.reviewSharePct}%` : `#${row.bestPosition}`}
      </Text>
    </Flex>
  );
}
